export class ClockSync {
    maxSamples;
    bestOf;
    samples = [];
    constructor(maxSamples = 12, bestOf = 3) {
        this.maxSamples = maxSamples;
        this.bestOf = bestOf;
    }
    /** Record one ping round trip. All times in epoch ms. */
    addSample(clientSentAt, serverTime, clientReceivedAt) {
        const rtt = Math.max(0, clientReceivedAt - clientSentAt);
        const offset = serverTime - (clientSentAt + clientReceivedAt) / 2;
        const sample = { offset, rtt };
        this.samples.push(sample);
        if (this.samples.length > this.maxSamples)
            this.samples.shift();
        return sample;
    }
    get sampleCount() {
        return this.samples.length;
    }
    get isSynced() {
        return this.samples.length >= this.bestOf;
    }
    /** Estimated server-minus-local offset in ms (0 until the first sample). */
    get offset() {
        if (this.samples.length === 0)
            return 0;
        const best = [...this.samples].sort((a, b) => a.rtt - b.rtt).slice(0, this.bestOf);
        const offsets = best.map((s) => s.offset).sort((a, b) => a - b);
        return offsets[Math.floor(offsets.length / 2)] ?? 0;
    }
    /** Best (lowest) round trip seen, a rough bound on sync accuracy (±rtt/2). */
    get bestRtt() {
        if (this.samples.length === 0)
            return null;
        return Math.min(...this.samples.map((s) => s.rtt));
    }
    /** Server time now, according to this device. */
    serverNow(localNow = Date.now()) {
        return localNow + this.offset;
    }
    /** Convert a server-clock timestamp to this device's clock. */
    toLocal(serverTime) {
        return serverTime - this.offset;
    }
    /** Milliseconds from now until a server-clock time (negative if passed). */
    msUntil(serverTime, localNow = Date.now()) {
        return this.toLocal(serverTime) - localNow;
    }
}
/**
 * Run a burst of pings to fill a ClockSync. `ping` must resolve with the
 * server's time; `now` is injectable for tests.
 */
export async function syncClock(clock, ping, options = {}) {
    const { samples = 8, gapMs = 60, now = Date.now } = options;
    for (let i = 0; i < samples; i++) {
        const sent = now();
        const serverTime = await ping(sent);
        clock.addSample(sent, serverTime, now());
        if (gapMs > 0 && i < samples - 1)
            await new Promise((r) => setTimeout(r, gapMs));
    }
    return clock;
}
//# sourceMappingURL=clock.js.map